/**
 * DO NOT MODIFY THIS FILE (Unless you really really have to).
 *
 * The Design System team is working on standardizing theming within the
 * Designer (and Editor).
 *
 * We're going to provide clear boundaries, mechanisms, and guidelines
 * to help folks understand the space and to be able to make changes with
 * confidence.
 *
 * In the meantime, if you really have to make changes, please reach
 * out to us!
 *
 * @since 2022-02-07
 * @link #design-system (Slack)
 * @see https://webflow.atlassian.net/browse/PDTY-2171
 * @see https://docs.google.com/document/d/1OOHFaJhsxbJVqXT7Zxk_Z79yzvpeGiw1mMqXua3NMAI
 */

import * as React from 'react';
import {defaults} from 'lodash';
import designer from './designer';
import SmallMarkIcon from './legacy/icons/CheckMedium';
import DashIcon from './legacy/icons/Indeterminate';
import ExternalLink from './legacy/icons/NewTabMedium';
import {white, black, rgba, fade} from '@packages/utilities/inline-style-utils';
import tinycolor from 'tinycolor2';
import {palette} from '@packages/utilities/styled-tokens';

import type {Theme} from './Theme';

// Should be a string, as in Theme type if field in present in the designer theme it is expected to be not undefined, but a string
const NO_VALUE = '';

const {focusColors, zIndexes, fontFamilies, shadows, fontWeights} = designer;

const {
  gray25,
  gray50,
  gray100,
  gray200,
  gray300,
  gray400,
  gray600,
  gray700,
  blue100,
  blue200,
  blue300,
  blue400,
  blue600,
  green200,
  green300,
  green400,
  pink300,
  pink500,
  red300,
  red400,
  red500,
  purple300,
  purple400,
  purple600,
  orange300,
  orange400,
  yellow400,
  whiteFade90,
} = palette.colors;

const colors = defaults(
  {
    background: white(),
    borderDeep: gray100,
    borderWithOpacity: rgba(51, 51, 51, 0.3),
    backgroundMiniNote: gray25,
    backgroundVivid: white(),
    backgroundVividest: gray50,
    control: gray50, // Hover effect for selects
    borderVivid: gray100,
    backgroundDeep: white(),
    backgroundDeepAlt: gray50,
    backgroundLight: gray50,
    accentBackground: green200,
    foreground: gray600, // text
    foregroundAlphaDim: fade(0.65)(gray600),
    foregroundDim: gray300, // dim text
    foregroundDimmer: gray200, //dimmer text
    hoverBlue: blue200,
    deepBackground: gray100,
    foregroundHeading: gray600,
    foregroundVivid: gray600,
    backgroundDeepest: gray50,
    border: gray25,
    icon: gray200,
    cms: {
      staged: yellow400,
      published: green300,
      archived: gray100,
    },
    /**
     * Values used for `properties` were copy-pasted from the Designer's theme
     * (packages/utilities/styled/designer.js). Then, we modified `default.foreground`
     * to look better (see the Slack thread linked below), since it was difficult to
     * read `<PropertyLabel>` text in such color (it was too light grey on a white
     * background).
     *
     * Other colors defined below were adjusted as well to match the light theme
     * of Editor better, but please be aware they seem to not be used anywhere in
     * Editor yet, therefore the color choice is definitely not final.
     *
     * @see https://webflow.slack.com/archives/C012XUU5EQL/p1691501234481729
     */
    properties: {
      local: {
        foreground: blue300,
        icon: blue300,
        background: blue300,
      },
      inherited: {
        foreground: orange400,
        icon: orange400,
        background: orange300,
      },
      default: {
        foreground: gray700,
        icon: 'transparent',
        background: 'transparent',
      },
      overridden: {
        foreground: gray700,
        icon: red500,
        background: red300,
      },
      id: {
        foreground: pink500,
        icon: pink500,
        background: pink300,
      },
    },
    successAccent: green200,
  },
  designer.colors
);

const control = defaults(
  {
    textColor: gray600,
    hoverTextColor: white(),
    backgroundColor: gray25,
    borderColor: gray100,
    foreground: gray100,
    borderRadius: 3,
    borderRadiusMiniNote: 2,
    height: 35,
    heightLarge: 35,
  },
  designer.control
);

const sansSerifFontStack =
  'Inter ,-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", sans-serif';

const typography = defaults(
  {
    heading: {
      fontSize: 20,
      fontFamily: sansSerifFontStack,
      lineHeight: 24,
      fontWeight: 400,
    },
    subheading: {
      fontSize: 14,
      fontFamily: sansSerifFontStack,
      lineHeight: 18,
      fontWeight: 600,
    },
    copy: {
      fontSize: 14,
      fontFamily: sansSerifFontStack,
      lineHeight: 18,
      fontWeight: 400,
    },
    finePrint: {
      fontSize: 12,
      fontFamily: sansSerifFontStack,
      lineHeight: 18,
      fontWeight: 400,
    },
  },
  designer.typography
);

const lineHeights = defaults(
  {
    heading: 60,
  },
  designer.lineHeights
);

const StyledSmallMarkIcon = (props: any) => (
  <SmallMarkIcon
    {...props}
    style={{
      position: 'relative',
      ...props.style,
    }}
  />
);

const dropdown = defaults(
  {
    backgroundColor: white(),
    borderColor: gray100,
    groupVerticalPadding: 6,
    horizontalPadding: designer.gutters.x12,
    dividerColor: colors.deepBackground,
  },
  designer.dropdown
);

const card = {
  borderRadius: 3,
} as const;

const gutters = defaults(
  {
    base: 6,
    narrow: 10,
    wide: 16,
  },
  designer.gutters
);

const itemsByCollectionModal = {
  backgroundColor: white(),
  borderColor: gray100,
} as const;

const pageSettings = {
  expandGroup: {
    freestanding: true,
    borderWidth: control.borderWidth,
  },
} as const;

const TextInput = {
  background: colors.backgroundDeep,
  borderColor: colors.borderDeep,
  borderColorHover: colors.borderDeep,
  boxShadow: '0 0 0 rgba(0,0,0,0)',
  color: 'inherit',
  hover: {
    background: white(),
  },
  focus: {
    background: white(),
  },
  disabled: {
    background: gray700,
    color: gray200,
  },
} as const;

const Divider = {
  backgroundColor: gray200,
  vivid: {
    backgroundColor: gray100,
  },
  subtle: {
    backgroundColor: gray100,
  },
  dark: {
    backgroundColor: gray50,
  },
} as const;

const Modal = {
  background: colors.backgroundVivid,
  backgroundDark: colors.backgroundDeepAltest,
  borderRadius: control.borderRadiusModal,
  closeButtonWidth: 32,
} as const;

const buttonShadow =
  '0 1px 2px rgba(0, 0, 0, 0.03), inset 0 -27px 70px -37px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.25)';

const Button = {
  size: {
    large: 36,
    small: 30,
  },
  gutters: {
    large: gutters.base * 2,
    small: gutters.narrow,
  },
  fontSize: {
    large: 14,
    small: 12,
  },
  borderWidth: control.borderWidth,
  boxShadow: buttonShadow,
  color: colors.foreground,
  paddingRight: 10,
  paddingLeft: 10,
  padding: 10,
  primary: {
    color: white(),
    background: green300,
    backgroundHover: green200,
    borderColor: green400,
  },
  subtle: {
    background: white(),
    backgroundHover: gray50,
    borderColor: gray50,
    borderColorHover: gray50,
    color: gray700,
    boxShadow: null,
    borderWidth: 0,
    borderStyle: 'none',
  },
  danger: {
    color: white(),
    background: red400,
    backgroundHover: red300,
    borderColor: red500,
  },
  risk: {
    color: white(),
    background: red400,
    backgroundHover: red300,
    borderColor: red500,
  },
  disabled: {
    opacity: 0.5,
    borderColor: colors.borderDeep,
    boxShadow: buttonShadow,
  },
  warning: {
    color: white(),
    background: orange400,
    backgroundHover: orange300,
    borderColor: orange400,
  },
  blend: {
    backgroundHover: 'rgba(0, 0, 0, 0.02)',
  },
  dynamic: {
    color: white(),
    background: purple400,
    backgroundHover: purple300,
    borderColor: purple600,
  },
  emphasis: {
    color: white(),
    borderColor: blue600,
    background: blue400,
    backgroundHover: blue300,
  },
  hover: {
    default: gray600,
    disabledColor: gray600,
    color: white(),
  },
  selected: {
    ghostBackground: colors.backgroundDeep,
  },
  buttonShellHeight: 'unset',
} as const;

const TableControl = {
  color: blue200,
  iconMargin: gutters.x8,
  alternate: {
    isButton: true,
  },
} as const;

const PropList = {
  borderColor: gray100,
  backgroundColor: white(),
  backgroundHover: colors.backgroundDeepAlt,
  color: gray700,
  fontSize: 14,
  lineHeight: '20px',
  item: {
    paddingTop: gutters.x12,
    paddingRight: gutters.x16 + 70, // 70px is max-width of the ControlsHolder when all 3 controls are there
    paddingBottom: gutters.x12,
    paddingLeft: gutters.x16,
  },
} as const;

const Table = {
  header: {
    paddings: {
      paddingTop: gutters.x12,
      paddingBottom: gutters.x12,
      paddingRight: gutters.x12,
      paddingLeft: gutters.x12,
    },
    ...designer.typography.subheading,
    lineHeight: designer.typography.subheading.lineHeight + 'px',
    background: gray50,
  },
  row: {
    ...designer.Table.row,
    paddings: {
      paddingTop: gutters.x12,
      paddingBottom: gutters.x12,
      paddingRight: gutters.x12,
      paddingLeft: gutters.x12,
    },
    hover: {
      background: blue100,
      borderColor: blue100,
      color: '#363636',
      iconWidth: 0,
    },
  },
} as const;

const ExpandableTable = {
  row: {
    hover: {
      iconWidth: 48,
    },
  },
} as const;

const EmptyState = {
  background: gray50,
  hoverBackgroundColor: gray25,
  border: colors.borderDeep,
  borderHover: colors.borderDeep,
  padding: 8 * gutters.x4,
  heading: {
    fontSize: 18,
  },
} as const;

const TreeModal = {
  treeBackground: white(),
  borderColor: gray100,
  rowHeight: 36,
  searchMarginBottom: gutters.x12,
} as const;

const InputWithSearch = {
  inputBackground: white(),
  iconColor: NO_VALUE,
} as const;

const ShippingMethodDropdown = {
  noteLineCountByShippingMethodMode: {
    'flat-rate': 3,
    percentage: 3,
    price: 3,
    quantity: 3,
    weight: 3,
  },
  defaultNoteLineCount: 2,
} as const;

const CountryModal = {
  width: 540,
} as const;

const Toggle = {
  boxShadow: `0 1px 3px 0 ${black(0.05)}`,
  background: `linear-gradient(180deg, transparent, ${black(
    0.02
  )}), linear-gradient(${white()}, ${white()})`,
  hover: {
    background: `linear-gradient(180deg, transparent, ${black(
      0.02
    )}), linear-gradient(${white()}, ${white()})`,
  },
  selected: {
    background: gray50,
  },
  disabled: {
    borderColor: gray100,
  },
} as const;

const ExternalAction = (props: any) => (
  <ExternalLink
    {...props}
    style={{
      marginLeft: gutters.base,
      ...props.style,
    }}
  />
);
const TodoList = {
  backgroundColor: white(),
  padding: gutters.wide,
  textPadding: gutters.base * 2,
  borderColor: gray100,
  actionText: {
    fontSize: typography.copy.fontSize,
    fontWeight: typography.copy.fontWeight,
    lineHeight: typography.copy.lineHeight,
    fontFamily: typography.copy.fontFamily,
    color: blue200,
  },
  iconsStyle: {
    checkboxChecked: {
      flexShrink: 0,
      marginTop: 0,
    },
    checkboxUnchecked: {
      flexShrink: 0,
      color: gray200,
      marginTop: 0,
    },
    externalAction: {
      marginLeft: gutters.base,
    },
  },
  borderBottomStyle: 'none',
} as const;

const CommerceGettingStartedChecklist = {
  width: '100%',
  displaySection: 'none',
  displayButton: 'inline',
  borderBottomStyle: 'none',
  paddingLeft: 0,
  paddingRight: 0,
  paddingTop: gutters.base * 4,
  checklistSectionPaddingBottom: 0,
  headingFontSize: typography.heading.fontSize,
  titleMargin: gutters.x16,
  checklistPaddingBottom: gutters.x12 * 2,
  noteContainerPaddingTop: gutters.x12 * 2,
} as const;

const CommerceCheckoutSettingsWarningNote = {
  borderColor: colors.borderDeep,
  icons: {
    externalAction: ExternalAction,
  },
} as const;

const CommercePaymentSettingsApplePayIcon = {
  color: black(),
} as const;

const FileList = {
  color: gray700,
  borderColor: gray100,
  backgroundColor: white(),
  gridAutoRows: `${gutters.base * 6}px auto`,
  fontSize: typography.copy.fontSize,
  icon: {
    color: gray100,
    padding: `0px ${gutters.base}px 0px ${gutters.base * 2}px`,
  },
} as const;

const FilePreview = {
  fontSize: typography.finePrint.fontSize,
} as const;

const FilePreviewLarge = {
  fontSize: typography.copy.fontSize,
} as const;

const Collapsible = {
  ...designer.Collapsible,
  backgroundColor: gray25,
  borderWidth: 1,
  boxShadow: undefined,
  horizontalPadding: gutters.base * 2,
  verticalPadding: gutters.base * 2,
} as const;

const CommerceProductOptions = {
  ...designer.CommerceProductOptions,
  newOptionColor: blue400,
} as const;

const OptionSetInput = {
  hoverColor: white(),
} as const;

const OptionPill = {
  ...designer.OptionPill,
  normal: {
    height: control.heightLarge - gutters.x8,
    color: whiteFade90,
    background: `linear-gradient(${green200}, #2daf69)`,
    border: `1px solid ${green300}`,
    boxShadow: 'none',
    hover: {
      color: whiteFade90,
      background: `linear-gradient(${[
        tinycolor(green200).lighten(10).toString(),
        tinycolor('#2daf69').lighten(10).toString(),
      ].join(', ')})`,
      border: `1px solid ${green300}`,
    },
    padding: gutters.base / 2,
    margin: gutters.base / 2,
  },
  menuIndicator: {
    border: `1px solid ${green300}`,
  },
} as const;

const Text = {
  headingColor: colors.foreground,
  labelColor: colors.foreground,
} as const;

const counter = {
  color: white(),
} as const;

const ExternalActionIcon = (props: any) => (
  <ExternalLink
    {...props}
    style={{
      marginLeft: gutters.x4,
      ...props.style,
    }}
  />
);
const Note = {
  lineHeight: typography.copy.lineHeight,
  fontSize: typography.copy.fontSize,
  fontWeight: typography.copy.fontWeight,
  paddingY: gutters.x8,
  paddingX: gutters.x12,
  colors: {
    neutral: colors.foreground,
    actionNeutral: blue400,
    warning: yellow400,
    success: colors.successAccent,
    error: red300,
    border: colors.borderDeep,
    background: colors.background,
  },
  iconSize: 18,
  externalActionIcon: ExternalActionIcon,
} as const;

const MiniImageMultiInput = {
  placeholderIconColor: colors.foreground,
  placeholderIconBackgroundColor: colors.background,
  width: 36,
  height: 36,
  filePopoverBtnWidth: 24,
  filePopoverBtnHeight: 36,
} as const;

const MultiImagePreview = {
  ...designer.MultiImagePreview,
  headingColor: gray700,
  boxShadow: `0 0 0 1px ${colors.borderDeep}`,
} as const;

const select = {height: 35} as const;

const adminTheme: Theme = {
  ...designer,
  name: 'admin',
  globalStyleTagStyles: '',
  colors,

  control,

  typography,

  lineHeights,

  Button,
  TableControl,

  table: {
    header: {
      row: {
        backgroundColor: gray50,
        borderColor: gray100,
        height: designer.table.header.row.height,
        fontSize: typography.finePrint.fontSize,
        fontWeight: typography.subheading.fontWeight,
        fontFamily: typography.subheading.fontFamily,
      },
      cell: {
        selected: gray400,
      },
    },
    row: {
      height: designer.table.row.height,
      disabled: {
        backgroundColor: white(),
        color: gray100,
      },
    },
  },

  dropdown,

  card,

  counter,

  highlight: () => blue100,

  gutters,

  itemsByCollectionModal,

  fontSizes: {
    ...designer.fontSizes,
    copy: 14,
  },

  protectedItems: {
    borderColor: gray100,
    backgroundColor: white(),
  },

  checkbox: {
    borderRadius: 4,
    size: (designer.control.height / 3) * 2,
    icons: {
      checkmark: StyledSmallMarkIcon,
      dashmark: DashIcon,
    },
    borderColor: {
      normal: gray100,
      checked: gray100,
      disabled: gray100,
      accent: colors.accentVivid,
    },
    color: {
      hover: colors.foregroundDim,
      checked: black(),
      accent: colors.accent,
      dashed: colors.foregroundDim,
    },
    background: {
      normal: white(),
      checked: white(),
      focus: white(),
      disabled: gray50,
    },
  },

  popover: {
    ...designer.popover,
    backgroundColor: white(),
    backgroundColorDark: white(),
  },

  tooltip: {
    ...designer.tooltip,
    textColor: gray600,
    statefulTextColor: white(),
    alternateBackgroundColor: gray50,
    hoverBackgroundColor: white(),
    alternateColor: gray600,
    error: {
      textColor: whiteFade90,
      backgroundColor: red400,
    },
  },

  pageSettings,

  MiniNote: {
    background: white(),
    color: gray700,
    fontSize: 14,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: colors.borderDeep,
    accentStripeWidth: 2,
    stripeCoversBorder: true,
  },

  TextInput,
  Text,
  Divider,
  PropList,
  Table,
  EmptyState,
  TreeModal,
  Modal,
  InputWithSearch,
  // Directly use designer values
  focusColors,
  zIndexes,
  fontFamilies,
  fontWeights,
  shadows,
  ShippingMethodDropdown,
  CountryModal,
  Toggle,
  TodoList,
  FileList,
  CommerceGettingStartedChecklist,
  CommerceCheckoutSettingsWarningNote,
  CommercePaymentSettingsApplePayIcon,
  Note,
  FilePreview,
  FilePreviewLarge,
  Collapsible,
  CommerceProductOptions,
  OptionSetInput,
  OptionPill,
  MiniImageMultiInput,
  ExpandableTable,
  MultiImagePreview,
  select,
};

export default adminTheme;
